/**
 * Cart Animation v1.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * DOM-side animation for cart item removal.
 * Exposes window.Theme.animateAndRemoveCartItem(el, key) — called by
 * the remove button in snippets/cart-line-item.liquid.
 *
 * Delegates to the <cart-engine> for the actual cart mutation, keeping the
 * engine's API pure (zero DOM access).
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  window.Theme = window.Theme || {};

  /**
   * Animate a cart row out, then delegate the engine removal.
   *
   * Sequence:
   * 1. Capture row height and set --row-height CSS variable
   * 2. Add .removing class to trigger CSS animation
   * 3. Wait for animation completion (animationend event or timeout from CSS duration)
   * 4. Call window.cartEngine.removeItem(key) to update the cart
   *
   * @param {HTMLElement|null} el  - The [data-key] row element
   * @param {string} key           - Cart line item key
   * @returns {Promise<void>}
   */
  async function animateAndRemoveCartItem(el, key) {
    if (el) {
      // Capture height before animation collapses it
      el.style.setProperty('--row-height', `${el.offsetHeight}px`);
      el.classList.add('removing');

      // Wait for CSS animation to finish.
      // Primary: animationend event (exact, respects reduced-motion).
      // Fallback: parsed animationDuration + 20ms buffer (never blocks > ~220ms).
      await new Promise((resolve) => {
        const onEnd = () => resolve();
        el.addEventListener('animationend', onEnd, { once: true });

        // Safety fallback: read duration from computed style once class is added.
        // Under prefers-reduced-motion, animationDuration is "0s" → fallback fires in ~20ms.
        // Normal case: "0.2s" → fallback fires in ~220ms (but animationend fires first at ~200ms).
        const raw = getComputedStyle(el).animationDuration; // e.g. "0.2s"
        const ms = raw ? parseFloat(raw) * 1000 + 20 : 220;
        setTimeout(() => {
          el.removeEventListener('animationend', onEnd);
          resolve();
        }, ms);
      });
    }

    // Pure engine call — no DOM access inside cartEngine.removeItem()
    const engine = window.cartEngine;
    if (!engine) {
      console.warn('[cart-animation] window.cartEngine not found');
      return;
    }
    return engine.removeItem(key);
  }

  window.Theme.animateAndRemoveCartItem = animateAndRemoveCartItem;
})();
